import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { 
  Shield, 
  CheckCircle, 
  Database, 
  ClipboardCheck, 
  GraduationCap,
  FileText,
  Settings,
  Search,
  Users,
  Award
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

export const ServicesPage = () => {
  const services = [
    {
      id: "qms",
      icon: <Shield className="h-12 w-12 text-blue-600" />,
      title: "Quality Systems (QMS) Design & Remediation",
      serviceId: "Quality Systems (QMS)",
      description: "Comprehensive quality management systems that scale with your organization and withstand regulatory scrutiny.",
      deliverables: [
        "SOPs and work instructions aligned to your processes",
        "CAPA systems with root cause analysis frameworks", 
        "Change control procedures with risk assessment integration",
        "Deviation investigation protocols and trending systems",
        "Risk management implementation (ICH Q9)",
        "Nitrosamine risk assessments and control strategies"
      ],
      outcome: "A robust QMS that enables consistent operations and regulatory confidence."
    },
    {
      id: "validation",
      icon: <CheckCircle className="h-12 w-12 text-green-600" />,
      title: "Validation & Qualification",
      serviceId: "Validation & Qualification",
      description: "Risk-based validation strategies that ensure system reliability while optimizing resource allocation.",
      deliverables: [
        "Computer System Validation (CSV) programs",
        "Process validation protocols and reports",
        "Cleaning validation studies and ongoing verification",
        "Equipment qualification (IQ/OQ/PQ)",
        "Utilities validation (HVAC, water systems, compressed gases)",
        "Method validation and verification protocols"
      ],
      outcome: "Validated systems that consistently deliver reliable results and regulatory compliance."
    },
    {
      id: "data-integrity",
      icon: <Database className="h-12 w-12 text-purple-600" />,
      title: "Data Integrity (ALCOA+)",
      serviceId: "Data Integrity",
      description: "Comprehensive data governance frameworks that ensure complete, consistent, and enduring data quality.",
      deliverables: [
        "Data integrity gap assessments",
        "ALCOA+ control implementation",
        "Audit trail review and monitoring procedures", 
        "Electronic signature and Part 11 compliance",
        "Data governance policies and procedures",
        "Remediation strategies for legacy systems"
      ],
      outcome: "Trustworthy data systems that stand up to regulatory inspection and internal audit."
    },
    {
      id: "regulatory",
      icon: <ClipboardCheck className="h-12 w-12 text-orange-600" />,
      title: "Regulatory Readiness & Audits",
      serviceId: "Regulatory Readiness",
      description: "Inspection preparation and response management to ensure regulatory confidence and successful outcomes.",
      deliverables: [
        "Mock audit preparation and execution",
        "Pre-inspection readiness assessments",
        "Response management for FDA, MHRA, EMA inspections",
        "483 observation response strategies",
        "Regulatory intelligence and trend monitoring",
        "Inspector interaction training for key personnel"
      ],
      outcome: "Inspection-ready organizations with confidence to engage regulators effectively."
    },
    {
      id: "training",
      icon: <GraduationCap className="h-12 w-12 text-indigo-600" />,
      title: "Training & Culture Development",
      serviceId: "Training",
      description: "Role-based training programs that build competency and create a sustainable quality culture.",
      deliverables: [
        "GMP onboarding programs for new hires",
        "Role-specific training curricula and competency assessments",
        "Train-the-trainer programs for internal sustainability",
        "Training effectiveness evaluation and continuous improvement",
        "Quality culture assessment and development plans",
        "Knowledge management systems implementation"
      ],
      outcome: "Competent teams with strong quality culture that sustains compliance long-term."
    }
  ];

  const handleEnquireNow = (serviceId) => {
    // Redirect to contact page with pre-selected service
    window.location.href = `/contact?service=${encodeURIComponent(serviceId)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-navy-900 mb-6">
              Our <span className="text-blue-600">Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Comprehensive GMP consulting services designed to strengthen your quality systems, 
              accelerate regulatory readiness, and build sustainable compliance capabilities 
              across the product lifecycle.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div key={index} id={service.id} className={`grid lg:grid-cols-2 gap-12 items-start ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                
                {/* Content */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="flex items-center space-x-4 mb-6">
                    {service.icon}
                    <h2 className="text-3xl font-bold text-navy-900">{service.title}</h2>
                  </div>
                  
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-navy-900 mb-4">Key Deliverables:</h3>
                    <ul className="space-y-3">
                      {service.deliverables.map((deliverable, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{deliverable}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-blue-50 p-6 rounded-xl">
                    <h4 className="font-semibold text-navy-900 mb-2">Outcome:</h4>
                    <p className="text-gray-700">{service.outcome}</p>
                  </div>

                  <Button 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => handleEnquireNow(service.serviceId)}
                  >
                    Enquire Now
                  </Button>
                </div>

                {/* Visual */}
                <Card className={`shadow-xl border-0 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      <div className="text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-navy-900 rounded-full flex items-center justify-center mx-auto mb-4">
                          {service.icon}
                        </div>
                        <h3 className="text-2xl font-bold text-navy-900 mb-2">{service.title}</h3>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <FileText className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                          <div className="text-sm font-semibold text-navy-900">Documentation</div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <Settings className="h-8 w-8 text-green-600 mx-auto mb-2" />
                          <div className="text-sm font-semibold text-navy-900">Implementation</div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <Search className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                          <div className="text-sm font-semibold text-navy-900">Assessment</div>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <Users className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                          <div className="text-sm font-semibold text-navy-900">Training</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-navy-900 mb-6">
            Ready to Strengthen Your <span className="text-blue-600">GMP Systems</span>?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how our proven methodologies can enhance your compliance posture 
            and accelerate your regulatory readiness.
          </p>
          <Button 
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4"
            onClick={() => {
              window.open('https://calendar.app.google/58Xjc9eXVRcnqs24A', '_blank');
              setTimeout(() => {
                window.location.href = '/thanks';
              }, 100);
            }}
          >
            Book Free 30-Minute Consultation
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};